import { z } from 'zod';

/* ===========================================================================
 * ONE WORKER'S DAY ACROSS EVERY SITE — `GET /api/field/workers/:code/today`.
 *
 * A tablet sees only its own punches, so an operator at site B could not tell
 * that the worker in front of them checked out of site A an hour ago (or that
 * A's tablet, offline all morning, has not even said so yet). This is the
 * read-only answer: today's real check-ins and check-outs from every site and
 * device, oldest first, plus where the worker stands now.
 *
 * Its own module because field.schema.ts is at its 500-line budget; exported
 * from the same barrel, so importers say '@attendance/shared' either way. The
 * APK screen (apps/field/src/app/workerToday.ts) is built against this shape.
 * ======================================================================== */

export const FieldWorkerTodayEventSchema = z.object({
  /** Real punches only (`punchState.REAL_PUNCH`); breaks are not listed. */
  type: z.enum(['PUNCH_IN', 'PUNCH_OUT']),
  /** ISO instant — the event's serverTimestamp. */
  at: z.string(),
  worksiteName: z.string().nullable(),
  /** Recorded by the device that is asking. */
  sameDevice: z.boolean(),
  /** Not counted toward the day's rollup (flagged for review, not rejected). */
  flagged: z.boolean(),
});
export type FieldWorkerTodayEvent = z.infer<typeof FieldWorkerTodayEventSchema>;

export const FieldWorkerTodaySchema = z.object({
  /** The worker's employeeCode. */
  code: z.string(),
  name: z.string(),
  /** The company day key (YYYY-MM-DD in the company timezone — docs/time-and-dates.md). */
  day: z.string(),
  /**
   * From `currentPresence` — the punch state machine's reading, so last night's
   * open leg counts (a night shift is ON_SITE at 03:00). A worker on a break
   * is ON_SITE with `onBreak`, never LEFT.
   */
  status: z.enum(['ON_SITE', 'LEFT', 'NOT_IN']),
  /**
   * A break is open at the last site. Its own field rather than a fourth
   * status so an older APK, which rejects an unknown status as "office
   * unreachable", still reads the worker as on site.
   */
  onBreak: z.boolean(),
  /** Today's punches, plus the check-in of a leg carried over midnight. */
  events: z.array(FieldWorkerTodayEventSchema),
});
export type FieldWorkerToday = z.infer<typeof FieldWorkerTodaySchema>;
